import { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import HeroSection from "@/components/HeroSection";
import KpiSection from "@/components/KpiSection";
import ExpertiseSection from "@/components/ExpertiseSection";
import ContactSection from "@/components/ContactSection";
import ScrollBackground3D from "@/components/ScrollBackground3D";
import AccessGate from "@/components/AccessGate";
import TimelineSection from "@/components/TimelineSection";

const MapSection = lazy(() => import("@/components/MapSection"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NAV_ITEMS = [
  { id: "home", label: "Accueil" },
  { id: "projets", label: "Projets" },
  { id: "parcours", label: "Parcours" },
  { id: "expertises", label: "Expertises" },
  { id: "contact", label: "Contact" },
];

/* ── Lusion-style hamburger lines ── */
function HamburgerIcon({ isOpen }) {
  return (
    <div className="w-6 h-4 relative flex flex-col justify-between">
      <motion.span
        animate={isOpen ? { rotate: 45, y: 7 } : { rotate: 0, y: 0 }}
        transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
        className="block w-full h-[2px] bg-current origin-center"
      />
      <motion.span
        animate={isOpen ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }}
        transition={{ duration: 0.25, ease: [0.76, 0, 0.24, 1] }}
        className="block w-full h-[2px] bg-current origin-right"
      />
      <motion.span
        animate={isOpen ? { rotate: -45, y: -7 } : { rotate: 0, y: 0 }}
        transition={{ duration: 0.35, ease: [0.76, 0, 0.24, 1] }}
        className="block w-full h-[2px] bg-current origin-center"
      />
    </div>
  );
}

/* ── Fullscreen overlay ── */
const overlayVariants = {
  closed: { clipPath: "circle(0% at calc(100% - 48px) 32px)" },
  open: { clipPath: "circle(150% at calc(100% - 48px) 32px)" },
};

const linkVariants = {
  closed: { y: 40, opacity: 0 },
  open: (i) => ({
    y: 0,
    opacity: 1,
    transition: { delay: 0.25 + i * 0.07, duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  }),
};

function Navigation({ activeSection }) {
  const [menuOpen, setMenuOpen] = useState(false);

  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const handleNav = (id) => {
    setMenuOpen(false);
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }, 400);
  };

  return (
    <>
      {/* ── Fixed header bar ── */}
      <motion.header
        data-testid="main-navigation"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 1 }}
        className="fixed top-0 left-0 right-0 z-[60] pointer-events-none"
        style={{ mixBlendMode: menuOpen ? "normal" : "difference" }}
      >
        <div className="max-w-[1800px] mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
          {/* Logo - hidden when menu is open */}
          <a
            href="#home"
            onClick={(e) => { e.preventDefault(); handleNav("home"); }}
            data-testid="nav-brand"
            className={`pointer-events-auto transition-opacity duration-300 ${menuOpen ? 'opacity-0' : 'opacity-100'}`}
          >
            <img src="/logo-jd.png" alt="JD" className="h-12 md:h-14 w-auto" />
          </a>

          {/* Menu toggle */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="nav-menu-toggle"
            className="pointer-events-auto flex items-center gap-3 text-white group p-2"
          >
            <span className="text-[13px] tracking-[0.15em] uppercase font-medium opacity-70 group-hover:opacity-100 transition-opacity duration-300 hidden sm:inline">
              {menuOpen ? "Fermer" : "Menu"}
            </span>
            <div className="w-6 h-6 flex items-center justify-center">
              <HamburgerIcon isOpen={menuOpen} />
            </div>
          </button>
        </div>
      </motion.header>

      {/* ── Fullscreen nav overlay ── */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            key="nav-overlay"
            initial="closed"
            animate="open"
            exit="closed"
            variants={overlayVariants}
            transition={{ duration: 0.65, ease: [0.76, 0, 0.24, 1] }}
            className="fixed inset-0 z-[55] bg-[#0D1B2A] flex items-center"
            data-testid="nav-overlay"
          >
            <div className="w-full max-w-[1800px] mx-auto px-6 md:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-12 md:gap-0">
              {/* Logo in overlay */}
              <div className="absolute top-6 left-6 md:left-10">
                <img src="/logo-jd.png" alt="JD" className="h-12 md:h-14 w-auto" />
              </div>
              
              {/* Links */}
              <nav className="flex flex-col gap-2 md:gap-0 mt-16 md:mt-0">
                {NAV_ITEMS.map(({ id, label }, i) => (
                  <motion.a
                    key={id}
                    href={`#${id}`}
                    onClick={(e) => { e.preventDefault(); handleNav(id); }}
                    custom={i}
                    variants={linkVariants}
                    initial="closed"
                    animate="open"
                    data-testid={`nav-${id}`}
                    className="group flex items-center gap-5 py-2 md:py-3"
                  >
                    <span className="text-[11px] tracking-[0.2em] text-[#B07D62] font-mono opacity-60 w-6">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span
                      className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight transition-all duration-500 group-hover:translate-x-3 ${
                        activeSection === id
                          ? "text-[#B07D62]"
                          : "text-[#FAFAF8] group-hover:text-white"
                      }`}
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      {label}
                    </span>
                    <span className="hidden md:block h-[1px] bg-white/10 flex-1 max-w-[200px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                  </motion.a>
                ))}
              </nav>

              {/* Side info */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
                className="md:text-right flex flex-col gap-6 text-sm"
              >
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#415A77] mb-2">Contact</p>
                  <a href="mailto:julien@damore.ch" className="block text-[#FAFAF8] hover:text-[#B07D62] transition-colors duration-300">julien@damore.ch</a>
                  <a href="tel:+41765783408" className="block text-[#FAFAF8] hover:text-[#B07D62] transition-colors duration-300">076 578 34 08</a>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#415A77] mb-3">Réseaux</p>
                  <div className="flex gap-4 md:justify-end">
                    <a
                      href="https://www.linkedin.com/in/julien-d-amore-a8950697/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="nav-linkedin"
                      className="w-10 h-10 rounded-full border border-[#B07D62]/30 flex items-center justify-center text-[#B07D62] hover:text-[#415A77] hover:border-[#415A77]/50 transition-all duration-300"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect width="4" height="12" x="2" y="9"/><circle cx="4" cy="4" r="2"/></svg>
                    </a>
                    <a
                      href="https://www.instagram.com/julien.damore"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="nav-instagram"
                      className="w-10 h-10 rounded-full border border-[#B07D62]/30 flex items-center justify-center text-[#B07D62] hover:text-[#415A77] hover:border-[#415A77]/50 transition-all duration-300"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/></svg>
                    </a>
                    <a
                      href="https://www.facebook.com/profile.php?id=61576821341276"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="nav-facebook"
                      className="w-10 h-10 rounded-full border border-[#B07D62]/30 flex items-center justify-center text-[#B07D62] hover:text-[#415A77] hover:border-[#415A77]/50 transition-all duration-300"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function MapSkeleton() {
  return (
    <div className="w-full h-[80vh] bg-[#0D1B2A] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-2 border-[#B07D62]/30 border-t-[#B07D62] rounded-full animate-spin" />
        <p className="text-[#415A77] text-sm tracking-wider">Chargement de la carte...</p>
      </div>
    </div>
  );
}

function App() {
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsRes, statsRes] = await Promise.all([
          axios.get(`${API}/projects`),
          axios.get(`${API}/stats`),
        ]);
        setProjects(projectsRes.data.projects);
        setStats(statsRes.data);
      } catch (e) {
        console.error("Error fetching data:", e);
      }
    };
    fetchData();
  }, []);

  // Intersection observer for active section
  useEffect(() => {
    const sections = document.querySelectorAll("section[id]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3, rootMargin: "-10% 0px -10% 0px" }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  return (
    <AccessGate>
      <div className="min-h-screen bg-[#0D1B2A] text-[#FAFAF8]">
        {/* Full-page scroll-linked 3D background */}
        <ScrollBackground3D />

        <Navigation activeSection={activeSection} />

        {/* Content layer above 3D */}
        <div className="relative z-[1]">
          <section id="home">
            <HeroSection projectCount={projects.length} />
          </section>

        <section id="projets">
          <Suspense fallback={<MapSkeleton />}>
            <MapSection projects={projects} stats={stats} />
          </Suspense>
        </section>

        <section id="parcours">
          <KpiSection stats={stats} />
          <TimelineSection />
        </section>

        <section id="expertises">
          <ExpertiseSection />
        </section>

        <section id="contact">
          <ContactSection />
        </section>
        </div>
      </div>
    </AccessGate>
  );
}

export default App;
